import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {ReactComponent as SuccessIcon} from '@icons/successfully.svg';
import {Box, useMediaQuery} from '@mui/material';
import {useEffect, useMemo, useState} from 'react';
import TicketsInfo from '@/components/Organization/TablesAndTickets/AddTableTicket/TicketsInfo';
import PaymentTicketsInfo from '@/components/Organization/TablesAndTickets/AddTableTicket/PaymentTicketsInfo';
import AttendeesInfo from '@/components/Organization/TablesAndTickets/AddTableTicket/AttendeesInfo';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import {
  AddTableTicketFormValues,
  initialValues,
  validationSchema,
} from '@/components/Organization/TablesAndTickets/AddTableTicket/formConfiguration';
import {IOrganizationTablesAndTicketsRequest, IOrganizationTicketsTiers} from '@/utils/types';
import {OrganizationTableAndTicketsService, userStorage} from '@/utils/services';
import {
  AddTableTicketContext,
  AddTableTicketContextType,
} from '@/components/Organization/TablesAndTickets/AddTableTicket/OrganizationSignUpContext';
import Stepper from '@/shared/components/Stepper';
import {Formik} from 'formik';
import {PurchaseTicketsStyle} from './PurchaseTickets.styles';
import {
  ADD_TABLE_TICKET_STEP,
  addTableTicketSteps,
  getNewTableTicketValues,
} from '@/components/Organization/TablesAndTickets/AddTableTicket/helpers';
import {getCurrentEventSelector} from '@/store/organization-event';
import {
  clearPrivateTicketsTiers,
  getPrivateTablesAndTicketsTiersSelector,
  getPrivateTicketsTiers,
  getTablesAndTicketsTiersSelector,
  getTicketsTiers,
} from '@/store/organization-tables';
import {PAYMENT_METHOD, ROUTER_URL, STORAGE_KEY, USER_TYPE} from '@/utils/enums';
import TicketInfoMob from '@/components/Organization/TablesAndTickets/AddTableTicket/PaymentTicketsInfo/TicketInfoMob';
import {useLocation, useNavigate} from 'react-router-dom';
import {getUserSelector} from '@/store/user';
import Modal from '@/shared/components/Modal';
import {homeStyle} from '@/pages/User/Home/Home.styles';
import {useApiInstance} from '@/contexts/ApiInstanceContext';
import {orgAPI, userAPI} from '@/core/api';
import {useGetOrganizationId} from '@/utils/hooks';

const PurchaseTicketsPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const isFromPage = true;

  const [activeStep, setActiveStep] = useState(0);
  const [isSuccessModalOpen2, setIsSuccessModalOpen2] = useState(false);

  const {apiInstance, role} = useApiInstance();

  const currentEvent = useAppSelector(getCurrentEventSelector);
  const user = useAppSelector(getUserSelector);
  const ordinaryTicketsTiers = useAppSelector(getTablesAndTicketsTiersSelector) as IOrganizationTicketsTiers[];
  const privateTicketsTiers = useAppSelector(getPrivateTablesAndTicketsTiersSelector) as IOrganizationTicketsTiers[];

  const organizationId = useGetOrganizationId();
  const isMobile = useMediaQuery('(max-width: 639.98px)');
  const isLaptop = useMediaQuery('(min-width: 1200px)');

  const handleNextStep = () => setActiveStep(activeStep + 1);
  const handleBackStep = () => setActiveStep(activeStep - 1);

  const onSuccess = () => {
    setIsSuccessModalOpen2(true);
  };

  const formData = useMemo(() => {
    switch (activeStep) {
      case ADD_TABLE_TICKET_STEP.TICKET_INFO:
        return isMobile ? <TicketInfoMob /> : <TicketsInfo isFromPage={isFromPage} />;

      case ADD_TABLE_TICKET_STEP.PAYMENT:
        return <PaymentTicketsInfo isFromPage={isFromPage} />;

      case ADD_TABLE_TICKET_STEP.ATTENDEES_INFO:
        return <AttendeesInfo isFromPage={isFromPage} />;

      default:
        break;
    }
  }, [activeStep, isMobile]);

  const startStripeSession = async (dataReq: IOrganizationTablesAndTicketsRequest) => {
    try {
      const {data} = await OrganizationTableAndTicketsService.prepareStripeSessionForTickets(dataReq, apiInstance);
      return data;
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const submitClickHandler = async (values: AddTableTicketFormValues) => {
    if (!organizationId) return;

    const newTicketValues: IOrganizationTablesAndTicketsRequest = getNewTableTicketValues(values);

    if (activeStep === ADD_TABLE_TICKET_STEP.ATTENDEES_INFO) {
      if (values.paymentOption === PAYMENT_METHOD.CREDIT_CARD) {
        const data = await startStripeSession(newTicketValues);
        if (data?.url) {
          window.location.href = data.url;
        }
      } else {
        try {
          await OrganizationTableAndTicketsService.createTableAndTickets(newTicketValues, orgAPI);
          toast.success('Table and tickets created successfully');
          navigate('/organization/attendees');
        } catch (error) {
          toast.error(getErrorMessage(error));
        }
      }
    } else {
      handleNextStep();
    }
  };

  const submitAttendeeClickHandler = async (values: AddTableTicketFormValues) => {
    if (!user) return;

    const newTicketValues: IOrganizationTablesAndTicketsRequest = getNewTableTicketValues(values);
    if (activeStep === ADD_TABLE_TICKET_STEP.ATTENDEES_INFO) {
      if (values.paymentOption === PAYMENT_METHOD.CREDIT_CARD) {
        const data = await startStripeSession(newTicketValues);
        if (data?.url) {
          window.location.href = data.url;
        }
      } else {
        try {
          await OrganizationTableAndTicketsService.createTableAndTickets(newTicketValues, userAPI);
          onSuccess();
        } catch (error) {
          toast.error(getErrorMessage(error));
        }
      }
    } else {
      handleNextStep();
    }
  };

  const AddTableTicketContextValue: AddTableTicketContextType = useMemo(() => {
    return {
      activeStep,
      handleBackStep,
    };
  }, [activeStep]);

  useEffect(() => {
    if (isLaptop && role === USER_TYPE.ORGANIZATION) {
      navigate(ROUTER_URL.ORGANIZATION_DASHBOARD);
    }
  }, [isLaptop]);

  useEffect(() => {
    if (!location.state?.fromPrivateLink) {
      dispatch(clearPrivateTicketsTiers());
      // userStorage.remove(STORAGE_KEY.PRIVATE_PURCHASE_TOKEN);
    }
  }, [location.state]);

  useEffect(() => {
    if (privateTicketsTiers || ordinaryTicketsTiers || !currentEvent?.id) return;

    if (location.state?.fromPrivateLink) {
      const token = userStorage.get(STORAGE_KEY.PRIVATE_PURCHASE_TOKEN);
      const data = {
        eventId: currentEvent.id,
        token: String(token),
        apiInstance: userAPI,
      };
      dispatch(getPrivateTicketsTiers(data)).unwrap();
    } else {
      dispatch(getTicketsTiers({reqBody: currentEvent.id, apiInstance: userAPI})).unwrap();
    }
  }, [privateTicketsTiers, ordinaryTicketsTiers, location.state, currentEvent?.id]);

  return (
    <AddTableTicketContext.Provider value={AddTableTicketContextValue}>
      <Box sx={PurchaseTicketsStyle.wrapper}>
        <Stepper steps={addTableTicketSteps} activeStep={activeStep} sx={PurchaseTicketsStyle.stepper} />

        <Formik
          initialValues={initialValues}
          onSubmit={role === USER_TYPE.USER ? submitAttendeeClickHandler : submitClickHandler}
          validationSchema={validationSchema(activeStep, isMobile)}
          validateOnChange={false}
        >
          {formData}
        </Formik>
      </Box>
      <Modal
        open={isSuccessModalOpen2}
        onClose={() => {
          setIsSuccessModalOpen2(false);
          navigate(-1);
        }}
        title="Thank You For Your Support!"
        description="Your tickets have been successfully purchased. A receipt and additional information will be delivered by email."
        sx={homeStyle.modalLogin}
        mainButtonTitle="Done"
        onSave={() => {
          setIsSuccessModalOpen2(false);
          navigate(-1);
        }}
        isShowSaveButton
        mainIcon={<SuccessIcon />}
      />
    </AddTableTicketContext.Provider>
  );
};

export default PurchaseTicketsPage;
